import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import Cropper from 'react-easy-crop';
import { toast } from 'react-toastify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to create image from URL
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

// Helper function to get cropped image
async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
}

export default function MentorsManagement() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [mentors, setMentors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const { getToken } = useAdminAuth();

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Create/Edit Modal State
  const [mentorForm, setMentorForm] = useState({
    full_name: '',
    email: '',
    bio: '',
    avatar_url: '',
    specialties: '',
    linkedin_url: '',
    twitter_url: '',
    website_url: '',
    status: 'active'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchMentors();
  }, [statusFilter]);

  const fetchMentors = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/mentors?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      setMentors(response.data.mentors);
    } catch (error) {
      console.error('Error fetching mentors:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona un archivo de imagen');
      return;
    }

    // Validar tamaño
    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 10MB');
      return;
    }

    // Read file and show crop modal
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = async () => {
    if (!croppedAreaPixels || !imageSrc) return;

    try {
      // Get cropped image blob
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      
      // Store the cropped blob for later upload
      setSelectedPhotoFile(croppedBlob);
      
      // Create preview from blob
      const previewUrl = URL.createObjectURL(croppedBlob);
      setPhotoPreview(previewUrl);
      
      toast.success('Foto recortada correctamente', {
        position: "top-center",
        autoClose: 2000,
      });
      
      setShowCropModal(false);
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (error) {
      console.error('Error cropping image:', error);
      toast.error('Error al recortar la imagen');
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleCreateMentor = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Convert specialties string to array
      const specialtiesArray = mentorForm.specialties
        .split(',')
        .map(s => s.trim())
        .filter(s => s);

      // Crear mentor primero
      const createResponse = await axios.post(
        `${BACKEND_URL}/api/admin/mentors`,
        {
          ...mentorForm,
          specialties: specialtiesArray
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      
      // Extract mentor ID from response
      const newMentorId = createResponse.data.mentor?.id || createResponse.data.mentor_id || createResponse.data.id;
      
      if (!newMentorId) {
        console.error('No mentor ID returned from create:', createResponse.data);
        toast.error('Error: No se pudo obtener el ID del mentor');
        return;
      }
      
      // Si hay foto seleccionada, subirla
      if (selectedPhotoFile && newMentorId) {
        setUploadingPhoto(true);
        console.log('Uploading photo for mentor:', newMentorId);
        try {
          const formData = new FormData();
          formData.append('file', selectedPhotoFile, 'avatar.jpg');

          const uploadResponse = await axios.post(
            `${BACKEND_URL}/api/admin/mentors/${newMentorId}/upload-avatar`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${getToken()}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          console.log('Photo upload response:', uploadResponse.data);
          toast.success('✅ Mentor creado con foto exitosamente');
        } catch (photoError) {
          console.error('Error uploading photo:', photoError);
          console.error('Photo error response:', photoError.response?.data);
          toast.warning('Mentor creado pero hubo un error al subir la foto: ' + (photoError.response?.data?.detail || photoError.message));
        } finally {
          setUploadingPhoto(false);
        }
      } else {
        if (!selectedPhotoFile) {
          console.log('No photo file selected');
        }
        toast.success('Mentor creado exitosamente');
      }
      
      setShowCreateModal(false);
      setMentorForm({
        full_name: '',
        email: '',
        bio: '',
        avatar_url: '',
        specialties: '',
        linkedin_url: '',
        twitter_url: '',
        website_url: '',
        status: 'active'
      });
      setPhotoPreview(null);
      setSelectedPhotoFile(null);
      fetchMentors();
    } catch (error) {
      console.error('Error creating mentor:', error);
      toast.error(error.response?.data?.detail || 'Error al crear el mentor');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMentor = async (mentorId, mentorName) => {
    if (!window.confirm(`¿Estás seguro de eliminar al mentor "${mentorName}"?`)) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/mentors/${mentorId}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      alert('Mentor eliminado exitosamente');
      fetchMentors();
    } catch (error) {
      console.error('Error deleting mentor:', error);
      alert(error.response?.data?.detail || 'Error al eliminar el mentor');
    }
  };

  const handleToggleActiveMentor = async (mentorId, mentorName, currentStatus) => {
    const action = currentStatus ? 'desactivar' : 'activar';
    if (!window.confirm(`¿Estás seguro de ${action} al mentor "${mentorName}"?`)) {
      return;
    }

    try {
      const response = await axios.patch(
        `${BACKEND_URL}/api/admin/mentors/${mentorId}/toggle-active`,
        {},
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      alert(response.data.message);
      fetchMentors();
    } catch (error) {
      console.error('Error toggling mentor status:', error);
      alert(error.response?.data?.detail || 'Error al cambiar el estado del mentor');
    }
  };

  const getStatusBadge = (mentor) => {
    // Support both is_active (boolean) and status (string) fields
    const isActive = mentor.is_active !== undefined ? mentor.is_active : (mentor.status === 'active');
    
    const styles = {
      active: 'bg-green-500/10 text-green-400 border-green-500/30',
      inactive: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
      on_leave: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    };
    
    if (mentor.status) {
      return styles[mentor.status] || styles.active;
    }
    return isActive ? styles.active : styles.inactive;
  };

  const getStatusLabel = (mentor) => {
    // Support both is_active (boolean) and status (string) fields
    const isActive = mentor.is_active !== undefined ? mentor.is_active : (mentor.status === 'active');
    
    const labels = {
      active: 'Activo',
      inactive: 'Inactivo',
      on_leave: 'De Baja',
    };
    
    if (mentor.status) {
      return labels[mentor.status] || mentor.status;
    }
    return isActive ? 'Activo' : 'Inactivo';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Gestión de Mentores</h1>
          <p className="text-gray-400 mt-1">Administra los mentores y sus sesiones asignadas</p>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-6 py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#F4E4B7] transition-colors"
        >
          + Crear Mentor
        </button>
      </div>

      {/* Filters */}
      <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
        <div className="flex gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          >
            <option value="">Todos los status</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
            <option value="on_leave">De Baja</option>
          </select>
        </div>
      </div>

      {/* Mentors Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div>
            <p className="mt-4 text-gray-400">Cargando mentores...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mentors.map((mentor) => (
            <div
              key={mentor.id}
              className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30 hover:border-[#D4AF37]/50 transition-all cursor-pointer"
              onClick={() => navigate(`/admin/mentors/${mentor.id}`)}
            >
              <div className="flex items-start gap-4 mb-4">
                {mentor.avatar_url || mentor.photo_url ? (
                  <>
                    <img
                      src={mentor.avatar_url || mentor.photo_url}
                      alt={mentor.full_name}
                      className="w-16 h-16 rounded-full object-cover flex-shrink-0 border-2 border-[#D4AF37]"
                      onError={(e) => {
                        console.error('Error loading avatar:', mentor.avatar_url || mentor.photo_url);
                        e.target.style.display = 'none';
                        const fallback = e.target.nextElementSibling;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4E4B7] items-center justify-center text-black font-bold text-xl flex-shrink-0 hidden">
                      {mentor.full_name?.charAt(0).toUpperCase()}
                    </div>
                  </>
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4E4B7] flex items-center justify-center text-black font-bold text-xl flex-shrink-0">
                    {mentor.full_name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white mb-1 truncate">{mentor.full_name || mentor.name}</h3>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(mentor)}`}
                  >
                    {getStatusLabel(mentor)}
                  </span>
                </div>
              </div>

              <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                {mentor.bio || 'Sin biografía'}
              </p>

              {mentor.specialties && mentor.specialties.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {mentor.specialties.slice(0, 3).map((specialty, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-[#1a4d5c]/30 text-[#D4AF37] text-xs rounded"
                    >
                      {specialty}
                    </span>
                  ))}
                  {mentor.specialties.length > 3 && (
                    <span className="px-2 py-1 bg-[#1a4d5c]/30 text-gray-400 text-xs rounded">
                      +{mentor.specialties.length - 3}
                    </span>
                  )}
                </div>
              )}

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Email:</span>
                  <span className="text-white truncate ml-2">{mentor.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Sesiones Asignadas:</span>
                  <span className="text-[#D4AF37] font-bold">{mentor.assigned_sessions || 0}</span>
                </div>
                {mentor.rating && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Rating:</span>
                    <span className="text-yellow-400">⭐ {mentor.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-[#1a4d5c]/30 flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/mentors/${mentor.id}`);
                  }}
                  className="flex-1 px-3 py-2 bg-[#1a4d5c]/30 text-[#D4AF37] rounded hover:bg-[#1a4d5c]/50 transition-colors text-sm"
                >
                  Ver Detalle
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleActiveMentor(mentor.id, mentor.full_name || mentor.name, mentor.is_active);
                  }}
                  className={`px-3 py-2 rounded transition-colors text-sm ${
                    mentor.is_active 
                      ? 'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20' 
                      : 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                  }`}
                  title={mentor.is_active ? 'Desactivar mentor' : 'Activar mentor'}
                >
                  {mentor.is_active ? '🔒' : '✓'}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteMentor(mentor.id, mentor.full_name || mentor.name);
                  }}
                  className="px-3 py-2 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20 transition-colors text-sm"
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}

          {mentors.length === 0 && (
            <div className="col-span-3 text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-400">No hay mentores</h3>
              <p className="mt-1 text-sm text-gray-500">Crea tu primer mentor para comenzar.</p>
            </div>
          )}
        </div>
      )}

      {/* Create Mentor Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#0A0A0A] rounded-lg max-w-3xl w-full border border-[#1a4d5c]/30 p-6 my-8">
            <h3 className="text-2xl font-bold text-white mb-4">Crear Nuevo Mentor</h3>

            <form onSubmit={handleCreateMentor} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Nombre Completo *</label>
                  <input
                    type="text"
                    required
                    value={mentorForm.full_name}
                    onChange={(e) => setMentorForm({ ...mentorForm, full_name: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="Ej: Juan Pérez"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={mentorForm.email}
                    onChange={(e) => setMentorForm({ ...mentorForm, email: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="mentor@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Biografía</label>
                <textarea
                  value={mentorForm.bio}
                  onChange={(e) => setMentorForm({ ...mentorForm, bio: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                  placeholder="Breve descripción del mentor..."
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Especialidades (separadas por coma)</label>
                <input
                  type="text"
                  value={mentorForm.specialties}
                  onChange={(e) => setMentorForm({ ...mentorForm, specialties: e.target.value })}
                  className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                  placeholder="Leadership, Business, Tech"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Foto de Perfil (1:1 cuadrada)</label>
                  
                  {/* Preview */}
                  {photoPreview && (
                    <div className="mb-3 text-center">
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="w-24 h-24 rounded-full object-cover border-2 border-[#D4AF37] mx-auto"
                      />
                      <p className="text-xs text-green-400 mt-2">✓ Foto lista para subir</p>
                    </div>
                  )}
                  
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoSelect}
                    className="hidden"
                  />
                  
                  {/* Upload button */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white hover:border-[#D4AF37] transition-colors text-sm"
                  >
                    {photoPreview ? '✏️ Cambiar Foto' : '📷 Seleccionar Foto'}
                  </button>
                  <p className="text-xs text-gray-500 mt-1">Max 10MB - JPEG, PNG, WebP<br/>Se abrirá editor de recorte</p>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Status</label>
                  <select
                    value={mentorForm.status}
                    onChange={(e) => setMentorForm({ ...mentorForm, status: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                  >
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                    <option value="on_leave">De Baja</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">LinkedIn</label>
                  <input
                    type="url"
                    value={mentorForm.linkedin_url}
                    onChange={(e) => setMentorForm({ ...mentorForm, linkedin_url: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Twitter</label>
                  <input
                    type="url"
                    value={mentorForm.twitter_url}
                    onChange={(e) => setMentorForm({ ...mentorForm, twitter_url: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="https://twitter.com/..."
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Website</label>
                  <input
                    type="url"
                    value={mentorForm.website_url}
                    onChange={(e) => setMentorForm({ ...mentorForm, website_url: e.target.value })}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  disabled={isSubmitting || uploadingPhoto}
                  className="flex-1 px-6 py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#F4E4B7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (uploadingPhoto ? 'Subiendo foto...' : 'Creando...') : 'Crear Mentor'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setMentorForm({
                      full_name: '',
                      email: '',
                      bio: '',
                      avatar_url: '',
                      specialties: '',
                      linkedin_url: '',
                      twitter_url: '',
                      website_url: '',
                      status: 'active'
                    });
                  }}
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-3xl">
            <div className="bg-[#0A0A0A] rounded-lg border border-[#1a4d5c]/30 overflow-hidden">
              <div className="p-4 border-b border-[#1a4d5c]/30">
                <h3 className="text-2xl font-bold text-white">Ajustar Foto del Mentor</h3>
                <p className="text-sm text-gray-400 mt-1">Arrastra para mover, usa el slider para hacer zoom (1:1 aspecto cuadrado)</p>
              </div>

              {/* Cropper Area */}
              <div className="relative h-96 bg-black">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              {/* Controls */}
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Zoom: {zoom.toFixed(1)}x
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#D4AF37]"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleCropSave}
                    className="flex-1 px-6 py-3 bg-[#D4AF37] text-black font-bold rounded-lg hover:bg-[#F4E4B7] transition-colors"
                  >
                    Aplicar Recorte
                  </button>
                  <button
                    onClick={handleCropCancel}
                    className="px-6 py-3 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
